// web/wildlifeData.ts — the Wildlife Encyclopedia: static, lore-grounded entries
// for every creature the player can encounter in the WEB build. Pure data + types,
// no DOM and no sim imports beyond the index constants, so it is trivially testable
// and import-cheap.
//
// Entries are indexable by SPECIES.* (the 6 living species) and APEX_KIND.* (the 4
// bosses). The game session (web/game.ts) joins these with LIVE population counts;
// the UI (web/ui.ts) renders name / threat / drops / evolutionary opportunity.
//
// Each blurb is accurate to the creature's SIM NICHE (see src/sim/wildlife.ts,
// src/sim/ecosystemExample.ts defaultSpeciesConfigs/defaultApexConfig, and
// docs/LORE.md §"Wildlife" / §"Apex Creatures"): territorial ambushers, flying
// hunters, omnivore raiders, harmless scavengers, and the rare apex sieges.

import { SPECIES, APEX_KIND } from '../src/types.ts';

/** Threat tiers, ordered low → high. Drives the encyclopedia badge colour. */
export type ThreatLevel = 'Harmless' | 'Low' | 'Medium' | 'High' | 'Apex';

/**
 * One encyclopedia record. `id` is the SPECIES.* value for `kind: 'species'`
 * entries and the APEX_KIND.* value for `kind: 'apex'` entries — so the two
 * groups are independently indexable by their respective enums.
 */
export interface WildlifeEntry {
  readonly id: number;
  readonly name: string;
  readonly kind: 'species' | 'apex';
  /** One line, grounded in the creature's sim niche. */
  readonly blurb: string;
  readonly threat: ThreatLevel;
  /** What the brood salvages when it kills/loots this creature. */
  readonly drops: string;
  /** The evolutionary opportunity — what the brood GAINS by defeating it. */
  readonly evolution: string;
}

/**
 * The six living species, in SPECIES.* order, then the four apex bosses in
 * APEX_KIND.* order. The species block is index-aligned with SPECIES so
 * `WILDLIFE_ENCYCLOPEDIA[SPECIES.Bird]` is the Bird; apex entries follow and are
 * looked up via `apexEntry(APEX_KIND.*)`.
 */
export const WILDLIFE_ENCYCLOPEDIA: readonly WildlifeEntry[] = [
  // --- Living species (index-aligned with SPECIES.*) ---
  {
    id: SPECIES.Spider,
    name: 'Spider',
    kind: 'species',
    blurb: 'Territorial ambush predator — lurks near its lair and strikes any insect that wanders inside its web radius.',
    threat: 'Medium',
    drops: 'Combat & venom genes on death — the raw material of a hunting brood.',
    evolution: 'Loot its venom to give your Soldiers a toxic bite; its territorial instinct hardens your colony into ambushers.',
  },
  {
    id: SPECIES.Bird,
    name: 'Bird',
    kind: 'species',
    blurb: 'Flying migratory hunter — ranges the whole map, ignores walls, and migrates on when prey runs thin. Nowhere is truly safe.',
    threat: 'High',
    drops: 'Wing & sensing genes on death — flight and far-sight, hard-won from the sky.',
    evolution: 'Harvest its wings to lift your Flyers higher and faster; its keen eyes sharpen your colony\'s scouting range.',
  },
  {
    id: SPECIES.Rat,
    name: 'Rat',
    kind: 'species',
    blurb: 'Omnivore raider — hunts your members AND plunders food nodes, swarming toward the nearest colony anchor.',
    threat: 'High',
    drops: 'Limb & combat genes on death — endurance and brute force from a relentless scavenger.',
    evolution: 'Take its limbs for tougher, longer-legged workers; killing raiders staunches the food it would have stolen.',
  },
  {
    id: SPECIES.Frog,
    name: 'Frog',
    kind: 'species',
    blurb: 'Small territorial ambusher — a tighter, weaker Spider that snaps up insects straying near its damp patch.',
    threat: 'Low',
    drops: 'Venom & limb genes on death — modest toxins and spring-loaded legs.',
    evolution: 'An accessible early kill: its venom seeds your first toxic Soldiers without the Spider\'s bite-back.',
  },
  {
    id: SPECIES.Beetle,
    name: 'Beetle',
    kind: 'species',
    blurb: 'Armoured ground scavenger — harmless to your brood (never attacks) but a RESOURCE RIVAL, slowly draining food nodes near its drift.',
    threat: 'Harmless',
    drops: 'Carapace genes on death — thick, layered plating from a living tank.',
    evolution: 'Strip its carapace for stacking armour plates — the cheapest path to a defensive, raid-proof colony.',
  },
  {
    id: SPECIES.Dragonfly,
    name: 'Dragonfly',
    kind: 'species',
    blurb: 'Fast flying territorial hunter — darts at lightning speed onto insects, but only within the airspace it patrols around home.',
    threat: 'Medium',
    drops: 'Wing & movement genes on death — pure speed from the swiftest flyer alive.',
    evolution: 'Claim its wings for blistering Flyer speed and reach; few creatures move faster across the map.',
  },

  // --- Apex bosses (APEX_KIND.* order) ---
  {
    id: APEX_KIND.Huntsman,
    name: 'Huntsman',
    kind: 'apex',
    blurb: 'A colossal ground spider — a wide-roaming apex hunter that thins an entire colony\'s census before it falls.',
    threat: 'Apex',
    drops: 'A guaranteed legendary-grade gene to the colony that lands the most damage.',
    evolution: 'Slay it and the victor brood ascends — apex venom and predatory instinct no ordinary hunt can grant.',
  },
  {
    id: APEX_KIND.CrowMatriarch,
    name: 'Crow Matriarch',
    kind: 'apex',
    blurb: 'An apex flying bird — ranges the entire map, ignores every wall, and strikes from above where no defence reaches.',
    threat: 'Apex',
    drops: 'A guaranteed legendary-grade gene to the colony that lands the most damage.',
    evolution: 'Bring her down for unmatched aerial genes — the colony that does owns the sky.',
  },
  {
    id: APEX_KIND.RatKing,
    name: 'Rat King',
    kind: 'apex',
    blurb: 'An apex ground rat — besieges your nest and SUMMONS rat reinforcements mid-fight, grinding defenders down by attrition.',
    threat: 'Apex',
    drops: 'A guaranteed legendary-grade gene to the colony that lands the most damage.',
    evolution: 'Break the siege to loot apex endurance and combat genes — the spoils of outlasting a swarm.',
  },
  {
    id: APEX_KIND.PondLeviathan,
    name: 'Pond Leviathan',
    kind: 'apex',
    blurb: 'An apex frog — slow but enormously tanky, an area-denial titan that devours other wildlife as readily as your members.',
    threat: 'Apex',
    drops: 'A guaranteed legendary-grade gene to the colony that lands the most damage.',
    evolution: 'Whittle down its vast health for the rarest carapace and toxicity genes in the world.',
  },
] as const;

/** The six living-species entries, index-aligned with SPECIES.*. */
export const SPECIES_ENTRIES: readonly WildlifeEntry[] =
  WILDLIFE_ENCYCLOPEDIA.filter((e) => e.kind === 'species');

/** The four apex-boss entries, in APEX_KIND.* order. */
export const APEX_ENTRIES: readonly WildlifeEntry[] =
  WILDLIFE_ENCYCLOPEDIA.filter((e) => e.kind === 'apex');

/** Encyclopedia entry for a SPECIES.* value (undefined if out of range). */
export function speciesEntry(species: number): WildlifeEntry | undefined {
  return SPECIES_ENTRIES[species];
}

/** Encyclopedia entry for an APEX_KIND.* value (undefined if out of range). */
export function apexEntry(kind: number): WildlifeEntry | undefined {
  return APEX_ENTRIES[kind];
}
